import type {
  EditorNormalizationResult,
  EditorState,
  EditableContentUnit,
  RawComponent,
  RawContentSection,
  RawContentUnit,
  RawSection,
  RawVideoSection,
  TableUnitDensity,
  TableUnitStyle,
} from './editorTypes';
import { extractAltFromCaption } from '../imageAltText';
import { normalizeQuestionEditorState } from './questionNormalizerEditor';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeIdentifier(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  return normalizeString(value);
}

function normalizeBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function getRawString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function normalizeVideoIdArray(value: unknown) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => normalizeString(entry))
    .filter((entry): entry is string => Boolean(entry));
}

function toSectionArray(value: unknown): RawSection[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawSection[];
}

function toContentUnitArray(value: unknown): RawContentUnit[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawContentUnit[];
}

function getContentSection(section: RawSection): RawContentSection | null {
  const contentSection = section.content_section ?? section.contentSection;
  return isRecord(contentSection) ? (contentSection as RawContentSection) : null;
}

function getVideoSection(section: RawSection): RawVideoSection | null {
  const videoSection = section.video_section ?? section.videoSection;
  return isRecord(videoSection) ? (videoSection as RawVideoSection) : null;
}

function normalizeContentUnitType(value: unknown) {
  if (typeof value === 'number') {
    return value === 2
      ? 'IMAGE'
      : value === 3
        ? 'VIDEO'
        : value === 4
          ? 'IFRAME'
          : value === 5
            ? 'TABLE'
            : 'TEXT';
  }

  const normalized = normalizeString(value)?.toUpperCase();

  if (normalized === 'IMAGE') {
    return 'IMAGE';
  }

  if (normalized === 'VIDEO') {
    return 'VIDEO';
  }

  if (normalized === 'IFRAME') {
    return 'IFRAME';
  }

  if (normalized === 'TABLE') {
    return 'TABLE';
  }

  return 'TEXT';
}

function normalizeTableStyle(value: unknown): TableUnitStyle {
  const normalized =
    typeof value === 'number' ? String(value) : normalizeString(value)?.toUpperCase();

  if (normalized === '1' || normalized === 'STRIPED') {
    return 'striped';
  }

  if (normalized === '2' || normalized === 'BORDERED') {
    return 'bordered';
  }

  return 'plain';
}

function normalizeTableDensity(value: unknown): TableUnitDensity {
  const normalized =
    typeof value === 'number' ? String(value) : normalizeString(value)?.toUpperCase();

  return normalized === '1' || normalized === 'COMPACT' ? 'compact' : 'comfortable';
}

function normalizeImageSize(unit: RawContentUnit): EditableContentUnit['imageSize'] {
  const value = unit.image_size ?? unit.imageSize;

  if (typeof value === 'number') {
    return value === 1 ? 'SMALL' : value === 3 ? 'LARGE' : 'MEDIUM';
  }

  const normalized = normalizeString(value)?.toUpperCase();

  if (normalized === '1' || normalized === 'SMALL') {
    return 'SMALL';
  }

  if (normalized === '3' || normalized === 'LARGE') {
    return 'LARGE';
  }

  return 'MEDIUM';
}

function stripSimpleParagraphWrapper(html: string) {
  const trimmed = html.trim();
  const match = trimmed.match(/^<p[^>]*>([\s\S]*)<\/p>$/i);
  return match ? match[1].trim() : trimmed;
}

export function extractStandaloneEquationLatex(
  html: string | null | undefined,
): string | null {
  if (typeof html !== 'string') {
    return null;
  }

  const normalized = html.trim();

  if (normalized.length === 0) {
    return null;
  }

  const stripped = stripSimpleParagraphWrapper(normalized);

  // Be conservative: any remaining HTML tag content means this should stay TEXT.
  if (/<\/?[a-z][^>]*>/i.test(stripped)) {
    return null;
  }

  const match = stripped.match(/^\$\$([\s\S]+)\$\$$/);

  if (!match) {
    return null;
  }

  const latex = match[1].trim();
  return latex.length > 0 ? latex : null;
}

function normalizeSharedUnitFields(unit: RawContentUnit) {
  // A description stored only as a legacy `[alt: ...]` marker is surfaced as the
  // editable alt value, and the marker is taken out of the caption. Saving the
  // unit then writes it to alt_text — the one path that ever cleans up stored
  // data, without a migration.
  const extracted = extractAltFromCaption(normalizeString(unit.caption) ?? undefined);

  return {
    altText:
      normalizeString(unit.alt_text ?? unit.altText) ??
      extracted.altText ??
      undefined,
    caption: extracted.displayCaption || undefined,
    collapseByDefault:
      normalizeBoolean(unit.collapse_by_default ?? unit.collapseByDefault) ??
      false,
    disableAutoNumbering:
      normalizeBoolean(
        unit.disable_auto_numbering ?? unit.disableAutoNumbering,
      ) ?? false,
    heading: normalizeString(unit.heading) ?? undefined,
  };
}

function normalizeEditableUnit(
  unit: RawContentUnit,
  {
    sourceContentSectionIndex,
    sourceContentSectionId,
    sourceSectionIndex,
    sourceSectionId,
    unitIndex,
  }: {
    sourceContentSectionIndex: number;
    sourceContentSectionId?: string;
    sourceSectionIndex: number;
    sourceSectionId: string;
    unitIndex: number;
  },
): EditableContentUnit {
  const sharedFields = normalizeSharedUnitFields(unit);
  const unitId =
    normalizeIdentifier(unit.id) ??
    `editor-unit-${sourceSectionId}-${unitIndex + 1}`;
  const unitType = normalizeContentUnitType(unit.type);

  if (unitType === 'IMAGE') {
    return {
      ...sharedFields,
      dynamicImageUrl:
        normalizeString(unit.dynamic_image_url ?? unit.dynamicImageUrl) ??
        undefined,
      editorType: 'IMAGE',
      id: unitId,
      imageSize: normalizeImageSize(unit),
      imageUrl: normalizeString(unit.image_url ?? unit.imageUrl) ?? undefined,
      sourceContentSectionIndex,
      sourceContentSectionId,
      sourceSectionIndex,
      sourceSectionId,
    };
  }

  if (unitType === 'VIDEO') {
    return {
      ...sharedFields,
      editorType: 'VIDEO',
      id: unitId,
      sourceContentSectionIndex,
      sourceContentSectionId,
      sourceSectionIndex,
      sourceSectionId,
      videoId: normalizeString(unit.video_id ?? unit.videoId) ?? undefined,
    };
  }

  if (unitType === 'TABLE') {
    const tableUnit = unit.table_unit ?? unit.tableUnit;

    return {
      ...sharedFields,
      editorType: 'TABLE',
      id: unitId,
      sourceContentSectionIndex,
      sourceContentSectionId,
      sourceSectionIndex,
      sourceSectionId,
      tableDensity: normalizeTableDensity(tableUnit?.density),
      // Absent means a table authored before the field existed, which still
      // meant its first row as a header. An explicit false round-trips as false.
      tableHeaderRow:
        normalizeBoolean(tableUnit?.header_row ?? tableUnit?.headerRow) ?? true,
      tableHtml: getRawString(tableUnit?.html) ?? '',
      tableStickyHeader:
        normalizeBoolean(tableUnit?.sticky_header ?? tableUnit?.stickyHeader) ??
        false,
      tableStyle: normalizeTableStyle(tableUnit?.style),
    };
  }

  if (unitType === 'IFRAME') {
    const iframeUnit = unit.iframe_unit ?? unit.iframeUnit;

    return {
      ...sharedFields,
      editorType: 'IFRAME',
      id: unitId,
      iframeHeight: normalizeNumber(iframeUnit?.height) ?? 420,
      iframeSource: normalizeString(iframeUnit?.source) ?? undefined,
      iframeWidth: normalizeNumber(iframeUnit?.width) ?? 100,
      sourceContentSectionIndex,
      sourceContentSectionId,
      sourceSectionIndex,
      sourceSectionId,
    };
  }

  const originalText = getRawString(unit.text) ?? '';
  const latex = extractStandaloneEquationLatex(originalText);

  if (latex) {
    return {
      ...sharedFields,
      editorType: 'EQUATION',
      id: unitId,
      latex,
      originalText,
      sourceContentSectionIndex,
      sourceContentSectionId,
      sourceSectionIndex,
      sourceSectionId,
    };
  }

  return {
    ...sharedFields,
    editorType: 'TEXT',
    id: unitId,
    originalText,
    sourceContentSectionIndex,
    sourceContentSectionId,
    sourceSectionIndex,
    sourceSectionId,
    text: originalText,
  };
}

export function normalizeComponentForEditor(
  component: RawComponent,
): EditorNormalizationResult {
  const contentUnits: EditableContentUnit[] = [];
  const questionState = normalizeQuestionEditorState(component);
  const sections = toSectionArray(component.sections);
  let videoIds: string[] = [];
  let videoSectionId: string | undefined;
  let videoSectionUid: number | undefined;
  let videoSectionOwnerSectionId: string | undefined;

  sections.forEach((section, sectionIndex) => {
    const sourceSectionId =
      normalizeIdentifier(section.id) ??
      normalizeIdentifier(section.uid) ??
      `__generated-section-${sectionIndex + 1}`;
    const videoSection = getVideoSection(section);
    const contentSection = getContentSection(section);

    if (!videoSectionOwnerSectionId && videoSection) {
      videoIds = normalizeVideoIdArray(
        videoSection.video_ids ?? videoSection.videoIds,
      );
      videoSectionId = normalizeIdentifier(videoSection.id) ?? undefined;
      videoSectionUid = normalizeNumber(videoSection.uid);
      videoSectionOwnerSectionId = sourceSectionId;
    }

    if (!contentSection) {
      return;
    }
    const sourceContentSectionId =
      normalizeIdentifier(contentSection.id) ??
      normalizeIdentifier(contentSection.uid) ??
      `__generated-content-section-${sectionIndex + 1}`;
    const units = toContentUnitArray(
      contentSection.content_units ?? contentSection.contentUnits,
    );

    units.forEach((unit, unitIndex) => {
      contentUnits.push(
        normalizeEditableUnit(unit, {
          sourceContentSectionIndex: sectionIndex,
          sourceContentSectionId,
          sourceSectionIndex: sectionIndex,
          sourceSectionId,
          unitIndex,
        }),
      );
    });
  });

  return {
    baseComponent: component,
    contentUnits,
    preLectureQuestionSectionId: questionState.preLectureQuestionSectionId,
    preLectureQuestionSectionOwnerSectionId:
      questionState.preLectureQuestionSectionOwnerSectionId,
    preLectureQuestionSectionUid: questionState.preLectureQuestionSectionUid,
    preLectureQuestions: questionState.preLectureQuestions,
    questionSectionId: questionState.questionSectionId,
    questionSectionOwnerSectionId: questionState.questionSectionOwnerSectionId,
    questionSectionUid: questionState.questionSectionUid,
    questions: questionState.questions,
    videoIds,
    videoSectionId,
    videoSectionOwnerSectionId,
    videoSectionUid,
  };
}

export function createEditorState(component: RawComponent): EditorState {
  const normalized = normalizeComponentForEditor(component);

  return {
    ...normalized,
    isDirty: false,
    isSaving: false,
  };
}
